import { z } from 'zod';

/**
 * F30 — Team Chat (Scoped).
 *
 * The single source of truth for the chat data surface. Consumed by:
 *   - @bravemove/db schema CHECK constraints (verbatim text-enum lists)
 *   - apps/api services + routes + Socket.IO server
 *   - apps/mobile frontend client (primary surface)
 *
 * See plans/30_F30_team_chat_scoped.md for the 26 locked decisions. The most
 * load-bearing for this type surface:
 *   - D2: room scope discriminator 'team' | 'booking' (XOR via DB CHECK)
 *   - D5: 4 message types ('text' | 'poll' | 'booking_link' | 'system')
 *   - D6: system events persisted with author=NULL + metadata.system_event_kind
 *   - D8: soft-delete with deleted_by_role 5-value enum ('author' | 'captain' |
 *         'booker' | 'admin' | 'system')
 *   - D14: 2000-char body cap
 *   - D16: 50-page history default + max
 *   - D20: client_message_id UUID idempotency
 *   - D21: any-member polls; 2-6 options; 5 expiry presets + custom timestamp
 *
 * Forward-compat reservations in `chat_messages.metadata`:
 *   - `reply_to_message_id` for post-MVP reply/threading
 *   - `mentioned_user_ids` for post-MVP @-mentions
 *   - `attachments[]` for post-MVP media uploads
 *   - `reactions[]` (or sibling table) for post-MVP reactions
 */

// ─── Length / range bounds (mirror schema CHECK constraints) ─────────────────

export const CHAT_MESSAGE_BODY_MAX = 2000;
export const CHAT_DELETED_REASON_MAX = 200;
export const CHAT_HISTORY_PAGE_SIZE_DEFAULT = 50;
export const CHAT_HISTORY_PAGE_SIZE_MAX = 50;
export const CHAT_RECONNECT_BACKFILL_MAX = 200;

export const CHAT_POLL_QUESTION_MIN = 2;
export const CHAT_POLL_QUESTION_MAX = 280;
export const CHAT_POLL_OPTIONS_MIN = 2;
export const CHAT_POLL_OPTIONS_MAX = 6;
export const CHAT_POLL_OPTION_LABEL_MIN = 1;
export const CHAT_POLL_OPTION_LABEL_MAX = 80;

// Rate-limit defaults — service layer wires the actual counters.
export const CHAT_RATE_LIMIT_PER_USER_PER_ROOM_PER_MINUTE = 30;
export const CHAT_RATE_LIMIT_PER_USER_PER_MINUTE = 100;

// ─── Enums (text-literal unions kept in sync with schema CHECKs) ─────────────

export const CHAT_ROOM_TYPES = ['team', 'booking'] as const;
export type ChatRoomType = (typeof CHAT_ROOM_TYPES)[number];
export const ChatRoomTypeSchema = z.enum(CHAT_ROOM_TYPES);

export const CHAT_ROOM_STATUSES = ['active', 'locked', 'archived'] as const;
export type ChatRoomStatus = (typeof CHAT_ROOM_STATUSES)[number];
export const ChatRoomStatusSchema = z.enum(CHAT_ROOM_STATUSES);

export const CHAT_ROOM_LOCKED_REASONS = [
  'booking_completed',
  'booking_cancelled',
  'team_disbanded',
] as const;
export type ChatRoomLockedReason = (typeof CHAT_ROOM_LOCKED_REASONS)[number];
export const ChatRoomLockedReasonSchema = z.enum(CHAT_ROOM_LOCKED_REASONS);

export const CHAT_ROOM_MEMBER_STATUSES = ['active', 'left'] as const;
export type ChatRoomMemberStatus = (typeof CHAT_ROOM_MEMBER_STATUSES)[number];
export const ChatRoomMemberStatusSchema = z.enum(CHAT_ROOM_MEMBER_STATUSES);

export const CHAT_MESSAGE_TYPES = ['text', 'poll', 'booking_link', 'system'] as const;
export type ChatMessageType = (typeof CHAT_MESSAGE_TYPES)[number];
export const ChatMessageTypeSchema = z.enum(CHAT_MESSAGE_TYPES);

/**
 * Decision D8 — 5-value enum. `'booker'` admits booking-room moderation cleanly
 * (the booker has delete-any power in their booking-room, mirroring the team
 * captain's power in their team-room). `'admin'` + `'system'` are reserved for
 * a future trust+safety wave (Decision D23 — F30 ships no admin chat surface).
 */
export const CHAT_MESSAGE_DELETED_BY_ROLES = [
  'author',
  'captain',
  'booker',
  'admin',
  'system',
] as const;
export type ChatMessageDeletedByRole = (typeof CHAT_MESSAGE_DELETED_BY_ROLES)[number];
export const ChatMessageDeletedByRoleSchema = z.enum(CHAT_MESSAGE_DELETED_BY_ROLES);

/**
 * Decision D6 — extensible enum. F30 ships 6 kinds. F31 (skill matching) will
 * add `'match_result_recorded'`; F32 (open games) will add `'open_game_posted'`
 * + `'open_game_joiner_accepted'`. Renderers MUST tolerate unknown kinds
 * (degrade to a generic "Activity in this room" stripe).
 */
export const CHAT_SYSTEM_EVENT_KINDS = [
  'member_joined',
  'member_left',
  'member_removed_by_captain',
  'captaincy_transferred',
  'room_locked',
  'booking_confirmed',
] as const;
export type ChatSystemEventKind = (typeof CHAT_SYSTEM_EVENT_KINDS)[number];
export const ChatSystemEventKindSchema = z.enum(CHAT_SYSTEM_EVENT_KINDS);

export const CHAT_POLL_EXPIRY_PRESETS = ['1h', '6h', '24h', '7d', '30d'] as const;
export type ChatPollExpiryPreset = (typeof CHAT_POLL_EXPIRY_PRESETS)[number];
export const ChatPollExpiryPresetSchema = z.enum(CHAT_POLL_EXPIRY_PRESETS);

// ─── Read shapes (returned by REST + emitted by Socket.IO broadcasts) ────────

export type ChatRoom = {
  readonly id: string;
  readonly type: ChatRoomType;
  readonly teamId: string | null;
  readonly bookingId: string | null;
  readonly status: ChatRoomStatus;
  readonly lockedReason: ChatRoomLockedReason | null;
  readonly lockedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly countryCode: string;
};

export type ChatRoomMember = {
  readonly id: string;
  readonly roomId: string;
  readonly userId: string;
  readonly status: ChatRoomMemberStatus;
  readonly joinedAt: string;
  readonly leftAt: string | null;
  readonly mutedAt: string | null;
  readonly lastReadMessageId: string | null;
  readonly lastReadAt: string | null;
};

/**
 * Discriminated message union — clients dispatch on `type` to pick the renderer.
 * Soft-deleted messages strip body + metadata at the API edge (Decision D8).
 */
export type ChatTextMessage = {
  readonly id: string;
  readonly roomId: string;
  readonly type: 'text';
  readonly authorUserId: string;
  readonly body: string;
  readonly metadata: Readonly<Record<string, unknown>>;
  readonly clientMessageId: string | null;
  readonly deletedAt: string | null;
  readonly deletedByRole: ChatMessageDeletedByRole | null;
  readonly createdAt: string;
};

export type ChatPollMessage = {
  readonly id: string;
  readonly roomId: string;
  readonly type: 'poll';
  readonly authorUserId: string;
  /** `metadata.poll_id` is the FK to chat_polls.id — client refetches via `GET /me/chat/polls/:pollId`. */
  readonly metadata: { readonly poll_id: string };
  readonly clientMessageId: string | null;
  readonly deletedAt: string | null;
  readonly deletedByRole: ChatMessageDeletedByRole | null;
  readonly createdAt: string;
};

export type ChatBookingLinkMessage = {
  readonly id: string;
  readonly roomId: string;
  readonly type: 'booking_link';
  readonly authorUserId: string;
  /**
   * `metadata.booking_id` is the FK to bookings.id. Only the host / trainer /
   * venue staff may read the booking, so the server also snapshots what the
   * card shows (sport, venue, start) at share time; absent on older rows.
   */
  readonly metadata: {
    readonly booking_id: string;
    readonly snapshot_label?: string;
    readonly sport_name?: string;
    readonly venue_name?: string;
    readonly start_at?: string;
  };
  readonly clientMessageId: string | null;
  readonly deletedAt: string | null;
  readonly deletedByRole: ChatMessageDeletedByRole | null;
  readonly createdAt: string;
};

export type ChatSystemMessage = {
  readonly id: string;
  readonly roomId: string;
  readonly type: 'system';
  readonly authorUserId: null;
  readonly metadata: {
    readonly system_event_kind: ChatSystemEventKind | string;
    readonly [k: string]: unknown;
  };
  /** System messages are never soft-deleted — always null. */
  readonly deletedAt: null;
  readonly deletedByRole: null;
  readonly createdAt: string;
};

export type ChatMessage =
  | ChatTextMessage
  | ChatPollMessage
  | ChatBookingLinkMessage
  | ChatSystemMessage;

/**
 * F09 v2 (E19) — the identity of one message author, delivered as a sidecar so
 * the client renders sender names + the PLAYER tick without per-row lookups.
 * `avatarUrl = COALESCE(users.avatar_url, sports_profiles.avatar_url)` (one
 * server helper, `loadChatAuthors(db, ids)`).
 */
export type ChatAuthor = {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarUrl: string | null;
  /** The author's PLAYER tick. */
  readonly verified: boolean;
};

/** Author sidecar keyed by user id (`authors[senderUserId].verified`). */
export type ChatAuthorMap = Readonly<Record<string, ChatAuthor>>;

/** Cursor-paginated history page returned by `GET /me/chat/rooms/:roomId/messages`. */
export type ChatMessagePage = {
  readonly messages: ReadonlyArray<ChatMessage>;
  readonly nextCursor: string | null;
  readonly hasMore: boolean;
  /**
   * F09 v2 (E19) — every author referenced by `messages`, keyed by user id.
   */
  readonly authors: ChatAuthorMap;
};

/** "My rooms" list shape returned by `GET /me/chat/rooms`. */
/**
 * Display context for a room list row. Booking rooms: the venue, the booked
 * sport and start time; team rooms: the team name + sport. Lets clients label
 * rooms ("Padel Point Achrafieh · Wed 21 Oct, 09:30") instead of showing ids.
 */
export type ChatRoomContext = {
  readonly title: string;
  readonly sportName: string | null;
  readonly startAt: string | null;
  readonly timezone: string | null;
};

export type ChatRoomSummary = {
  readonly room: ChatRoom;
  readonly lastMessage: ChatMessage | null;
  readonly unreadCount: number;
  readonly myMembership: Readonly<{ mutedAt: string | null }>;
  /** Optional so hand-built fixtures stay valid; `GET /me/chat/rooms` always sets it when resolvable. */
  readonly context?: ChatRoomContext | null;
};

/** Poll detail returned by `GET /me/chat/polls/:pollId`. */
export type ChatPollOption = {
  readonly id: string;
  readonly label: string;
};

export type ChatPoll = {
  readonly id: string;
  readonly roomId: string;
  readonly messageId: string;
  readonly question: string;
  readonly options: ReadonlyArray<ChatPollOption>;
  readonly expiresAt: string;
  readonly creatorUserId: string | null;
  readonly createdAt: string;
  /** Per-option vote tally; option_id → count. Stays visible after expiry (Decision D21). */
  readonly voteCounts: Readonly<Record<string, number>>;
  /** The caller's vote (option_id) or null if they haven't voted. */
  readonly myVoteOptionId: string | null;
};

/** Per-message "read by" sheet entry. */
export type ChatMessageReadEntry = {
  readonly messageId: string;
  readonly userId: string;
  readonly readAt: string;
  readonly userDisplayName: string;
  readonly userAvatarUrl: string | null;
};

// ─── Zod input schemas ───────────────────────────────────────────────────────

/**
 * Decision D20 — client_message_id is a client-side UUID v4/v7. The server's
 * partial unique index `chat_messages_client_id_idx` on (room_id, client_message_id)
 * raises 23505 on replay; the service catches + returns the existing row with
 * `replayed: true` (REST header `X-Idempotent-Replay: true`).
 */
const ClientMessageIdSchema = z.string().uuid();

const TextBodySchema = z
  .string()
  .min(1, 'INVALID_MESSAGE_BODY')
  .max(CHAT_MESSAGE_BODY_MAX, 'MESSAGE_BODY_TOO_LONG');

/**
 * Discriminated send-message input. Type narrows the metadata + body shape.
 * The poll-message path requires the poll to already exist (created via
 * `POST /me/chat/rooms/:roomId/polls`); this schema only writes the parent
 * `chat_messages` row referencing the poll.
 */
export const SendChatTextMessageInputSchema = z.object({
  type: z.literal('text'),
  clientMessageId: ClientMessageIdSchema,
  body: TextBodySchema,
});

export const SendChatBookingLinkMessageInputSchema = z.object({
  type: z.literal('booking_link'),
  clientMessageId: ClientMessageIdSchema,
  metadata: z.object({
    booking_id: z.string().uuid(),
  }),
});

/** Server-only (not exposed via REST/WS) — internal entry point. */
export const SendChatSystemMessageInputSchema = z.object({
  type: z.literal('system'),
  metadata: z
    .object({
      system_event_kind: z.string(),
    })
    .passthrough(),
});

export const SendChatMessageInputSchema = z.discriminatedUnion('type', [
  SendChatTextMessageInputSchema,
  SendChatBookingLinkMessageInputSchema,
  // Poll-parent message is created in the same transaction as the poll itself —
  // see CreateChatPollInputSchema. Clients never send `type: 'poll'` directly
  // through this endpoint; doing so returns 400 INVALID_MESSAGE_TYPE.
  // System messages are also internal-only (Decision D6) — clients can't post
  // them via REST/WS.
]);

export type SendChatMessageInput = z.infer<typeof SendChatMessageInputSchema>;

export const DeleteChatMessageInputSchema = z.object({
  reason: z.string().max(CHAT_DELETED_REASON_MAX).optional(),
});

export type DeleteChatMessageInput = z.infer<typeof DeleteChatMessageInputSchema>;

/**
 * Decision D21 — poll creation. The server generates option-ids if not
 * supplied (deterministic slug per label). Expiry is either a preset OR an
 * explicit timestamp (`customExpiresAt`); custom must be > now() AND
 * ≤ 365 days out (defensive upper bound — D17 retention is indefinite but we
 * don't want UI-side "never-expire" polls at MVP).
 */
const ChatPollOptionInputSchema = z.object({
  id: z.string().min(1).max(40).optional(),
  label: z.string().min(CHAT_POLL_OPTION_LABEL_MIN).max(CHAT_POLL_OPTION_LABEL_MAX),
});

export const CreateChatPollInputSchema = z.object({
  clientMessageId: ClientMessageIdSchema,
  question: z.string().min(CHAT_POLL_QUESTION_MIN).max(CHAT_POLL_QUESTION_MAX),
  options: z.array(ChatPollOptionInputSchema).min(CHAT_POLL_OPTIONS_MIN).max(CHAT_POLL_OPTIONS_MAX),
  expiry: z.union([
    z.object({ preset: ChatPollExpiryPresetSchema }),
    z.object({ customExpiresAt: z.string().datetime() }),
  ]),
});

export type CreateChatPollInput = z.infer<typeof CreateChatPollInputSchema>;

export const CastChatPollVoteInputSchema = z.object({
  optionId: z.string().min(1).max(40),
});

export type CastChatPollVoteInput = z.infer<typeof CastChatPollVoteInputSchema>;

export const MarkChatRoomReadInputSchema = z.object({
  throughMessageId: z.string().uuid(),
});

export type MarkChatRoomReadInput = z.infer<typeof MarkChatRoomReadInputSchema>;

export const ToggleChatRoomMuteInputSchema = z.object({
  muted: z.boolean(),
});

export type ToggleChatRoomMuteInput = z.infer<typeof ToggleChatRoomMuteInputSchema>;

export const FetchChatHistoryQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(CHAT_HISTORY_PAGE_SIZE_MAX)
    .default(CHAT_HISTORY_PAGE_SIZE_DEFAULT)
    .optional(),
});

export type FetchChatHistoryQuery = z.infer<typeof FetchChatHistoryQuerySchema>;

// ─── Error codes (typed union for REST + Socket.IO error envelopes) ──────────

export const CHAT_ERROR_CODES = [
  'ROOM_NOT_FOUND',
  'ROOM_LOCKED',
  'ROOM_ARCHIVED',
  'NOT_ROOM_MEMBER',
  'NOT_AUTHORIZED_TO_DELETE',
  'MESSAGE_NOT_FOUND',
  'MESSAGE_ALREADY_DELETED',
  'INVALID_MESSAGE_BODY',
  'MESSAGE_BODY_TOO_LONG',
  'INVALID_MESSAGE_TYPE',
  'INVALID_METADATA',
  'BOOKING_LINK_NOT_ACCESSIBLE',
  'POLL_NOT_FOUND',
  'POLL_EXPIRED',
  'POLL_ALREADY_VOTED',
  'POLL_INVALID_OPTION',
  'POLL_OPTION_COUNT_INVALID',
  'POLL_QUESTION_INVALID',
  'IDEMPOTENT_REPLAY',
  'RATE_LIMITED',
  'WEBSOCKET_AUTH_FAILED',
  'WEBSOCKET_PROTOCOL_VIOLATION',
] as const;

export type ChatErrorCode = (typeof CHAT_ERROR_CODES)[number];

// ─── Socket.IO event shapes (server↔client wire protocol) ────────────────────

/**
 * Decision D18 — Socket.IO connection handshake carries the F07 access JWT.
 * The server-side middleware verifies + attaches `socket.data.user`.
 */
export type ChatSocketConnectAuth = {
  readonly token: string;
};

/** Client → Server event payloads. */
export type ChatSocketClientEvents = {
  readonly 'room:join': { roomId: string; since?: string };
  readonly 'room:leave': { roomId: string };
  readonly 'message:send': {
    roomId: string;
    clientMessageId: string;
    type: 'text' | 'booking_link';
    body?: string;
    metadata?: Readonly<Record<string, unknown>>;
  };
  readonly 'message:delete': { messageId: string; reason?: string };
  readonly 'message:mark_read': { messageId: string };
  readonly 'room:mute': { roomId: string };
  readonly 'room:unmute': { roomId: string };
  readonly 'poll:create': {
    roomId: string;
    clientMessageId: string;
    question: string;
    options: ReadonlyArray<ChatPollOption>;
    expiry: { preset: ChatPollExpiryPreset } | { customExpiresAt: string };
  };
  readonly 'poll:vote': { pollId: string; optionId: string };
};

/** Server → Client event payloads (broadcasts to room subscribers). */
export type ChatSocketServerEvents = {
  /**
   * F09 v2 (E19) — `author` is the sender's identity (PLAYER tick included),
   * added by BOTH emitters (`chat-message.ts` + `chat-poll.ts`); null for
   * system messages.
   */
  readonly 'message:created': { roomId: string; message: ChatMessage; author: ChatAuthor | null };
  readonly 'message:deleted': {
    roomId: string;
    messageId: string;
    deletedByRole: ChatMessageDeletedByRole;
    deletedAt: string;
  };
  readonly 'message:read': {
    roomId: string;
    messageId: string;
    userId: string;
    readAt: string;
  };
  readonly 'room:locked': {
    roomId: string;
    reason: ChatRoomLockedReason;
    lockedAt: string;
  };
  readonly 'room:member_added': { roomId: string; member: ChatRoomMember };
  readonly 'room:member_removed': {
    roomId: string;
    userId: string;
    kind: 'self_left' | 'team_removed' | 'team_disbanded';
  };
  readonly 'poll:created': { roomId: string; poll: ChatPoll; messageId: string };
  readonly 'poll:vote_cast': {
    pollId: string;
    userId: string;
    optionId: string;
    voteCounts: Readonly<Record<string, number>>;
  };
};

/** Ack payload returned by server for client events that include an ack callback. */
export type ChatSocketAck<T extends Record<string, unknown> = Record<string, never>> =
  | (T & { readonly ok: true; readonly replayed?: boolean })
  | { readonly ok: false; readonly errorCode: ChatErrorCode; readonly message?: string };

/**
 * The success payload of the `room:join` ack (`ChatSocketAck<ChatRoomJoinAckPayload>`).
 * F09 v2 (E19) adds the `authors` sidecar for `missedMessages`
 * (`chat-socket-server.ts`).
 */
export type ChatRoomJoinAckPayload = {
  readonly missedMessages: ReadonlyArray<ChatMessage>;
  readonly authors: ChatAuthorMap;
};
